import React, { useState } from "react";
import { Form, Input, Button, Table, Popconfirm, Space, message } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";

type Props = {
  categories: any[];
  loadingCats: boolean;
  createCat: any;
  updateCat: any;
  deleteCat: any;
};

export const ExpenseCategory: React.FC<Props> = ({
  categories,
  loadingCats,
  createCat,
  updateCat,
  deleteCat,
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      createCat.mutate(
        { name: values.name.trim() },
        {
          onSuccess: (res: any) => {
            if (res.success) {
              message.success("Category created successfully!");
              form.resetFields();
            } else {
              message.error(res.message || "Failed to create category.");
            }
          },
          onError: () => message.error("Something went wrong."),
        },
      );
    } catch {
      message.error("Please enter a category name.");
    }
  };

  const handleSaveEdit = async (record: any, newName: string) => {
    if (!newName.trim()) {
      message.error("Category name cannot be empty.");
      return;
    }
    updateCat.mutate(
      { id: record.id, name: newName.trim() },
      {
        onSuccess: (res: any) => {
          if (res.success) {
            message.success("Category updated successfully!");
            setEditingId(null);
          } else {
            message.error(res.message || "Failed to update category.");
          }
        },
        onError: () => message.error("Something went wrong."),
      },
    );
  };

  const handleDelete = async (id: string) => {
    deleteCat.mutate(id, {
      onSuccess: (res: any) => {
        if (res.success) {
          message.success("Category deleted successfully!");
        } else {
          message.error(res.message || "Failed to delete category.");
        }
      },
      onError: () => message.error("Something went wrong."),
    });
  };

  const columns = [
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: any) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Input
            defaultValue={text}
            id={`cat-input-${record.id}`}
            size="small"
            onPressEnter={(e: any) => handleSaveEdit(record, e.target.value)}
            style={{ width: "100%" }}
          />
        ) : (
          <span className="font-medium text-gray-700">{text}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, record: any) => {
        const isEditing = record.id === editingId;
        return isEditing ? (
          <Space size="middle">
            <Button
              type="text"
              icon={<SaveOutlined className="text-green-600" />}
              size="small"
              onClick={() => {
                const input = document.getElementById(
                  `cat-input-${record.id}`,
                ) as HTMLInputElement;
                if (input) handleSaveEdit(record, input.value);
              }}
            />
            <Button
              type="text"
              icon={<CloseOutlined className="text-gray-400" />}
              size="small"
              onClick={() => setEditingId(null)}
            />
          </Space>
        ) : (
          <Space size="middle">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => setEditingId(record.id)}
            />
            <Popconfirm
              title="Are you sure you want to delete this category?"
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Form
        form={form}
        layout="inline"
        className="my-4 flex items-center justify-between"
      >
        <Form.Item
          name="name"
          rules={[{ required: true, message: "Category name is required" }]}
          style={{ flex: 1, marginRight: 8 }}
        >
          <Input placeholder="e.g. Utilities, Rent" prefix={<PlusOutlined />} />
        </Form.Item>
        <Form.Item style={{ marginRight: 0 }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleCreate}
            loading={createCat.isPending}
          >
            Add Category
          </Button>
        </Form.Item>
      </Form>

      <Table
        dataSource={categories}
        columns={columns}
        rowKey="id"
        size="small"
        loading={loadingCats}
        pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
        bordered
      />
    </>
  );
};
